<script setup lang="ts">
/**
 * Compact week overview (Phase 6). Sits above the day view. Shows date-stamped
 * day rows with status badges + rest styling, gold week nav, week progress, and
 * current-week highlight. Tapping a day emits `select(week, dayOfWeek)`; editing
 * stays in the day view. Pure data comes from weekView.ts.
 */
import { computed, ref, watch } from 'vue'
import type { PlanData } from '@/features/ai-plan/lib/types'
import type { CheckedMap } from '@/shared/bridge/trainingBridge'
import { getWeekRows, weekProgress, currentWeek, type WeekDayStatus } from '@/features/training/lib/weekView'

const props = defineProps<{
  plan: PlanData
  checked: CheckedMap
  today: string
  activeWeek: number
  activeDayOfWeek: number
}>()

const emit = defineEmits<{ select: [week: number, dayOfWeek: number] }>()

const totalWeeks = computed(() => Math.max(1, props.plan.total_weeks || 1))
const displayedWeek = ref(props.activeWeek)

// Follow the day view when the active week changes elsewhere (e.g. plan change).
watch(() => props.activeWeek, w => { displayedWeek.value = w })

const rows = computed(() => getWeekRows(props.plan, displayedWeek.value, props.checked, props.today))
const progress = computed(() => weekProgress(props.plan, displayedWeek.value, props.checked))
const curWeek = computed(() => currentWeek(props.plan, props.today, props.checked))
const isCurrentWeek = computed(() => displayedWeek.value === curWeek.value)

const STATUS_META: Record<WeekDayStatus, { icon: string; text: string }> = {
  'complete': { icon: '✓', text: 'Done' },
  'in-progress': { icon: '◐', text: 'In progress' },
  'not-started': { icon: '○', text: 'Not started' },
}

function fmtDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ymd
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function prevWeek() { if (displayedWeek.value > 1) displayedWeek.value-- }
function nextWeek() { if (displayedWeek.value < totalWeeks.value) displayedWeek.value++ }
function select(dayOfWeek: number) { emit('select', displayedWeek.value, dayOfWeek) }

function isActive(dayOfWeek: number): boolean {
  return displayedWeek.value === props.activeWeek && dayOfWeek === props.activeDayOfWeek
}
function ariaFor(label: string, date: string, status: WeekDayStatus): string {
  return `Week ${displayedWeek.value}, ${fmtDate(date)}, ${label}, ${STATUS_META[status].text}`
}
</script>

<template>
  <div class="week-view">
    <div class="week-view__head">
      <button type="button" class="week-nav" aria-label="Previous week"
              :disabled="displayedWeek <= 1" @click="prevWeek">‹</button>
      <div class="week-view__title">
        <span class="week-view__wk">Week {{ displayedWeek }}<span class="week-view__of"> / {{ totalWeeks }}</span></span>
        <span v-if="isCurrentWeek" class="week-view__current">This week</span>
      </div>
      <button type="button" class="week-nav" aria-label="Next week"
              :disabled="displayedWeek >= totalWeeks" @click="nextWeek">›</button>
    </div>

    <div class="week-view__progress" role="progressbar" :aria-valuenow="progress.pct" aria-valuemin="0" aria-valuemax="100">
      <div class="week-view__bar"><span :style="{ width: progress.pct + '%' }" /></div>
      <span class="week-view__progress-text">{{ progress.doneDays }} / {{ progress.totalDays }} days</span>
    </div>

    <ul class="week-view__days">
      <li v-for="row in rows" :key="row.dayOfWeek">
        <button
          type="button"
          class="day-chip"
          :class="[`is-${row.status}`, { 'is-active': isActive(row.dayOfWeek), 'is-rest': row.isRest }]"
          :aria-current="isActive(row.dayOfWeek) ? 'true' : undefined"
          :aria-label="ariaFor(row.label, row.date, row.status)"
          @click="select(row.dayOfWeek)"
        >
          <span class="day-chip__date">{{ fmtDate(row.date) }}</span>
          <span class="day-chip__label">
            {{ row.label }}<span v-if="row.isRest" class="day-chip__rest">Rest</span>
          </span>
          <span class="day-chip__status" :class="`is-${row.status}`">
            <span aria-hidden="true">{{ STATUS_META[row.status].icon }}</span>
            <span class="day-chip__status-text">{{ STATUS_META[row.status].text }}</span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.week-view {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.week-view__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.week-nav {
  min-width: 36px;
  min-height: 36px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: transparent;
  color: var(--accent);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.week-nav:disabled { opacity: 0.35; cursor: default; }
.week-view__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Barlow Condensed', sans-serif;
}
.week-view__wk { font-size: 17px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text); }
.week-view__of { color: var(--muted); }
.week-view__current {
  font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
  color: var(--accent); border: 1px solid var(--accent); border-radius: 999px; padding: 2px 8px;
}
.week-view__progress { display: flex; align-items: center; gap: 8px; }
.week-view__bar { flex: 1; height: 5px; border-radius: 3px; background: var(--border2); overflow: hidden; }
.week-view__bar span { display: block; height: 100%; background: var(--green); border-radius: 3px; transition: width 0.35s ease; }
.week-view__progress-text { flex-shrink: 0; font-size: 11px; color: var(--muted); font-variant-numeric: tabular-nums; }

.week-view__days { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.day-chip {
  width: 100%;
  min-height: 38px;
  display: grid;
  grid-template-columns: minmax(78px, auto) 1fr auto;
  align-items: center;
  gap: 10px;
  text-align: left;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-left: 3px solid var(--border2);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.day-chip.is-active { border-color: var(--accent); border-left-color: var(--accent); background: rgba(160, 120, 64, 0.08); }
.day-chip.is-complete { border-left-color: var(--green); }
.day-chip.is-in-progress { border-left-color: var(--accent); }
.day-chip.is-rest { opacity: 0.92; }
.day-chip__date { font-family: 'Barlow Condensed', sans-serif; font-weight: 700; font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase; color: var(--muted); white-space: nowrap; }
.day-chip__label { color: var(--text); font-size: 13px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.day-chip__rest {
  margin-left: 8px; font-size: 9px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
  color: var(--muted); border: 1px solid var(--border2); border-radius: 999px; padding: 1px 6px;
}
.day-chip__status { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: var(--muted); white-space: nowrap; }
.day-chip__status.is-complete { color: var(--green); }
.day-chip__status.is-in-progress { color: var(--accent); }

@media (max-width: 480px) {
  .day-chip__status-text { display: none; } /* keep the icon; drop text on narrow screens */
}
</style>
